/**
 * The day's files, summarised before the doctor sees each patient.
 *
 * The requirement: the secretary runs the AI over all of the day's files at
 * once (or a selection), and the summaries are waiting on the doctor's
 * dashboard. The server takes the batch and works through it one file at a
 * time in the background (routes/ai.js — a dozen analyses would outlive any
 * request), so this panel starts it, polls its progress, and lists what has
 * landed. The doctor's live view reads the same summaries; nothing is copied.
 *
 * A summary is written for the doctor as a starting point. It is not a
 * diagnosis, and the panel says so under every one.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, EmptyState, Icon } from '../../components/common';
import { aiBatchApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { usePoll } from '../../hooks/usePolling';
import { formatDateTime } from './labels';

const MAX_BATCH = 15;

export function AiBatchPanel({ bookings, date }) {
  const { success, error: toastError } = useNotifications();
  const [summaries, setSummaries] = useState([]);
  const [progress, setProgress] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [open, setOpen] = useState(null);
  const [starting, setStarting] = useState(false);

  const byBooking = useMemo(() => new Map(summaries.map((s) => [String(s.bookingId), s])), [summaries]);

  const loadSummaries = useCallback(() => aiBatchApi.summaries({ date }).then((r) => setSummaries(Array.isArray(r) ? r : [])), [date]);

  useEffect(() => {
    loadSummaries().catch(() => {});
    aiBatchApi
      .status()
      .then((r) => setProgress(r.progress))
      .catch(() => {});
  }, [loadSummaries]);

  // Default selection: everybody today without a summary yet.
  useEffect(() => {
    setPicked(new Set(bookings.filter((b) => !byBooking.has(String(b.id))).slice(0, MAX_BATCH).map((b) => String(b.id))));
  }, [bookings, byBooking]);

  const running = Boolean(progress && !progress.finishedAt);

  usePoll(
    async () => {
      const r = await aiBatchApi.status();
      setProgress(r.progress);
      await loadSummaries();
      if (r.progress?.finishedAt) success(`اكتمل التحليل: ${r.progress.done} ملف`);
    },
    running ? 4000 : null,
    { immediate: false },
  );

  const toggle = (id) =>
    setPicked((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_BATCH) next.add(id);
      return next;
    });

  const start = async () => {
    setStarting(true);
    try {
      const r = await aiBatchApi.start([...picked]);
      setProgress(r.progress);
      success(`بدأ تحليل ${r.queued.length} ملف — ستظهر الملخصات عند الطبيب تباعاً`);
    } catch (err) {
      toastError(err.message);
    } finally {
      setStarting(false);
    }
  };

  if (bookings.length === 0) {
    return <EmptyState icon="sparkles" title="لا توجد ملفات اليوم" message="يظهر هنا مرضى اليوم لتحليل ملفاتهم قبل دخولهم للطبيب." />;
  }

  const pct = progress && progress.total ? Math.round(((progress.done + progress.failed) / progress.total) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-purple-500/25 bg-purple-500/[0.07] p-4">
        <span className="tile3d__face tone-purple shrink-0" aria-hidden="true">
          <Icon name="sparkles" size={24} className="tile3d__icon" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-white">حلّل ملفات اليوم دفعة واحدة</p>
          <p className="text-xs text-slate-300">
            اختر حتى {MAX_BATCH} ملفاً. يصل ملخص كل مريض إلى شاشة الطبيب قبل دخوله.
          </p>
        </div>
        <Button icon="wand-sparkles" loading={starting} disabled={running || picked.size === 0} onClick={start}>
          تحليل {picked.size} ملف
        </Button>
      </div>

      {progress && (
        <div className="rounded-xl border border-white/5 bg-white/[0.03] p-3" aria-live="polite">
          <div className="mb-1 flex flex-wrap justify-between gap-2 text-xs text-slate-300">
            <span>{running ? 'جارٍ التحليل…' : 'اكتمل آخر تحليل'}</span>
            <span>
              {progress.done} تم · {progress.failed} تعذر · من {progress.total}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-gradient-to-l from-purple-400 to-cyan-400 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      <ul className="divide-y divide-white/5 rounded-2xl border border-white/5">
        {bookings.map((b) => {
          const s = byBooking.get(String(b.id));
          const id = String(b.id);
          return (
            <li key={b.id} className="p-3">
              <div className="flex flex-wrap items-center gap-3">
                <input
                  type="checkbox"
                  className="h-5 w-5"
                  checked={picked.has(id)}
                  disabled={running}
                  onChange={() => toggle(id)}
                  aria-label={`تحليل ملف ${b.patientName}`}
                />
                <span className="shrink-0 rounded-lg bg-slate-800 px-2 py-1 font-mono text-xs text-cyan-300">{b.time}</span>
                <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">{b.patientName}</span>
                {s ? (
                  <button type="button" onClick={() => setOpen(open === id ? null : id)} className="text-xs text-purple-300 underline">
                    <Badge className="border-purple-500/30 bg-purple-500/10 text-purple-200" icon="sparkles">
                      ملخص جاهز
                    </Badge>
                  </button>
                ) : (
                  <span className="text-xs text-slate-500">لم يُحلل</span>
                )}
              </div>
              {s && open === id && (
                <div className="mt-2 rounded-xl bg-purple-500/[0.06] p-3">
                  <p className="whitespace-pre-line text-sm text-slate-100">{s.text}</p>
                  <p className="mt-2 text-[11px] text-slate-500">
                    {formatDateTime(s.createdAt)} — ملخص مساعد للطبيب وليس تشخيصاً.
                  </p>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default AiBatchPanel;
